import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './sitesearch.module.css';
import AsyncSelect from 'react-select/async';
import { makeRequest } from '../../Utils';
import { useNavigate } from 'react-router-dom';

const selectStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: 'var(--theme-light)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '2rem',
    borderColor: state.isFocused || state.isHovered ? 'var(--theme-deep-blue)' : 'var(--theme-dark-accent)',
    minHeight: '32px',
    color: 'var(--theme-off-black)'
  }),
  container: (baseStyles, state) => ({
    ...baseStyles,
    width: '100%'
  }),
  input: (baseStyles, state) => ({
    ...baseStyles,
    color: 'var(--theme-off-black)',
    borderColor: 'none',
    fontFamily: 'NeueMontreal',
    fontSize: '16px'
  }),
  placeholder: (baseStyles, state) => ({
    ...baseStyles,
    color: 'var(--theme-dark-accent)'
  }),
  menu: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: 'var(--theme-main-background)',
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: '14px',
    fontFamily: 'NeueMontreal',
    fontWeight: state.isSelected ? '700' : '400',
    color: state.isSelected ? 'var(--theme-red)' : 'var(--theme-off-black)',
    backgroundColor: state.isFocused ? 'var(--theme-less-light)' : 'var(--theme-main-background)',
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    background: 'var(--theme-less-light)',
    fontFamily: 'NeueMontreal',
    color: 'var(--theme-off-black)',
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: '0px 8px'
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: '0px 8px'
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    fontFamily: 'NeueMontreal',
    color: 'var(--theme-off-black)',
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    fontFamily: 'NeueMontreal',
    fontSize: '14px',
    color: 'var(--theme-off-black)',
  }),
}

export default function SiteSearch() {
  const [query, setQuery] = useState('');
  const [publishers, setPublishers] = useState([]);
  const navigate = useNavigate();



  const loadOptions = useCallback(async (inputValue, callback) => {
    try {
      const lowerInput = inputValue.toLowerCase();
      const pubMatches = publishers
        .filter(pub => pub.name.toLowerCase().includes(lowerInput))
        .map(pub => ({
          type: 'publisher',
          value: pub.id,
          title: pub.name,
          label: pub.name,
          url: `/publisher/${pub.slug}`,
          image: pub.logo?.['300'] || null
        }));

      const defaultSearch = [{
        query: inputValue,
        type: 'default',
        url: `/search?query=${inputValue}`
      }];

      const data = await makeRequest(`search?term=${inputValue}&mode=quick`);
      const productOptions = data
        .filter(item => !item.variantOf)
        .map(item => ({
          type: 'product',
          value: item._id,
          label: `${item.title} (${item.format})`,
          authors: item.authors,
          title: item.title,
          format: item.format,
          image: item.cover?.['300'],
          url: `/product/${item.slug}`
        }));

      callback([...pubMatches, ...productOptions, ...defaultSearch]);
    } catch (error) {
      console.error("Error fetching data:", error);
      callback([]);
    }
  }, [publishers]);


  function debounceLoadOptions(fn, delay = 300) {
    let timeoutId;

    return (inputValue, callback) => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        fn(inputValue, callback);
      }, delay);
    };
  }
  const debouncedLoadOptions = useMemo(() => debounceLoadOptions(loadOptions, 300), [loadOptions]);



  const handleInputChange = (newInputValue, { action }) => {
    if (action === 'input-change') {
      setQuery(newInputValue);
    }
  };

  const handleChange = async (option) => {
    navigate(option ? option.url : '/');
    setQuery('');
  };

  useEffect(() => {
    const fetchPublishers = async () => {
      try {
        let data = await makeRequest('publishers');
        setPublishers(data);
      } catch (error) {
        console.error("Error fetching publishers:", error);
      }
    };
    fetchPublishers();
  }, [])

  return (
    <div className={styles.siteSearch}>
      <AsyncSelect
        value={null}
        inputValue={query}
        onChange={handleChange}
        defaultOptions={[]}
        loadOptions={debouncedLoadOptions}
        onInputChange={handleInputChange}
        styles={selectStyles}
        components={{ Option: CustomOption }}
        isClearable={true}
        placeholder={'Search by title, author, publisher, or ISBN'}
      />
    </div>
  )
}

const prepareAuthors = (authors) => {
  if (!authors || authors.length === 0) {
    return '';
  }
  if (authors.length > 3) {
    return `${authors[0]}, ${authors[1]}, et al.`;
  }
  return authors.join(', ');
};

const CustomOption = (props) => {
  const { data, innerRef, innerProps } = props;
  return (
    <div ref={innerRef} {...innerProps} className={styles.searchOption}>
      {data.type === 'publisher' ? PubOption(data) : data.type === 'default' ? DefaultOption(data) : BookOption(data)}
    </div>
  );
};

const DefaultOption = (data) => {
  return (
    <>
      <div className={styles.iconContainer}>
        <i className='fa-solid fa-magnifying-glass'></i>
      </div>
      <div>
        <div className={styles.optionTitle}>Search for <strong>{data.query}</strong>...</div>
      </div>
    </>
  )
}

const PubOption = (data) => {
  return (
    <>
      <div className={styles.optionImage}>
        <img src={data.image} alt={data.label} />
      </div>
      <div>
        <div className={styles.optionTitle}>{data.title}</div>
        <div className={styles.primaryMeta}>Publisher</div>
      </div>
    </>
  )
}

const BookOption = (data) => {
  return (
    <>
      <div className={styles.optionImage}>
        <img src={data.image} alt={data.label} />
      </div>
      <div>
        <div className={styles.secondaryMeta}>{data.format}</div>
        <div className={styles.optionTitle}>{data.title}</div>
        <div className={styles.primaryMeta}>{prepareAuthors(data.authors)}</div>
      </div>
    </>
  )
}
